import { TypedDocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";

import { SEARCH_VCS_INTEGRATIONS } from "../VcsList/gql";

export const useRefreshSecret = <T>(
  id: string,
  initialSecret: string,
  mutation: TypedDocumentNode<T, { id: string }>,
  mapper: (data: T) => string
): [string, () => Promise<boolean>] => {
  const [webhookSecret, setWebhookSecret] = useState<string>(initialSecret);
  const { onError } = useTypedContext(FlashContext);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [regenerateSecretMutation] = useMutation(mutation, {
    variables: { id },
    refetchQueries: [SEARCH_VCS_INTEGRATIONS],
    awaitRefetchQueries: true,
  });

  const regenerateSecret = useCallback(async () => {
    try {
      const result = await regenerateSecretMutation();
      if (!result.data) return false;

      const newSecret = mapper(result.data);
      if (newSecret && isMounted.current) setWebhookSecret(newSecret);

      return true;
    } catch (error) {
      onError(error);
      return false;
    }
  }, [regenerateSecretMutation, mapper, onError]);

  return [webhookSecret, regenerateSecret];
};
